import React, { useState } from 'react';
import { apiCall } from '../config';
import { useTranslation, LANGUAGES } from '../i18n/LanguageContext';
import ImpressumFooter from './ImpressumFooter';

const LuggageSearch = () => {
  const { t, language, setLanguage } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [stationInfo, setStationInfo] = useState(null);
  const [searched, setSearched] = useState(false);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shippingData, setShippingData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    ticketPhoto: null,
    dhlLabel: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitted, setSubmitted] = useState(null); // { id, email } nach erfolgreicher Anfrage

  // Station-Zuordnung basierend auf Ticketnummer
  const getStationInfo = (number) => {
    const num = parseInt(number);
    
    if (num <= 4000) {
      return {
        station: 'G1',
        name: 'Gepäckstation G1',
        googleMapsUrl: 'https://maps.app.goo.gl/W2QZcy54nJcWitmt6',   // derselbe Pin wie im WhatsApp-Bot
        description: 'Ihre Abholstation / Your pickup station'
      };
    } else if (num >= 5000 && num <= 6999) {
      return {
        station: 'G5',
        name: 'Gepäckstation G5', 
        googleMapsUrl: 'https://maps.app.goo.gl/qU3XSAKzDn2nQvFX9',
        description: 'Ihre Abholstation / Your pickup station'
      };
    } else if (num >= 7000 && num <= 9999) {
      return {
        station: 'G8',
        name: 'Gepäckstation G8',
        googleMapsUrl: 'https://maps.app.goo.gl/gay7fQH2t249jnJZA',
        description: 'Ihre Abholstation / Your pickup station'
      };
    } else if (num >= 10000 && num <= 12000) {
      return {
        station: 'G10',
        name: 'Gepäckstation G10',
        googleMapsUrl: 'https://maps.app.goo.gl/GGfYiPwn9nyqwoBw7',   // derselbe Pin wie im WhatsApp-Bot
        description: 'Ihre Abholstation / Your pickup station'
      };
    } else {
      return null;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (ticketNumber) {
      const station = getStationInfo(ticketNumber);
      setStationInfo(station);
      setSearched(true);
      setSubmitted(null);
    }
  };

  const handleFileChange = (field, file) => {
    setShippingData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitted(null);

    try {
      // Validierung
      if (!shippingData.name || !shippingData.email || !shippingData.address) {
        setSubmitMessage('❌ ' + t('lug.errRequired'));
        setIsSubmitting(false);
        return;
      }

      // FormData für File-Upload erstellen
      const formData = new FormData();
      
      // Basisdaten hinzufügen
      formData.append('ticketNumber', ticketNumber);
      formData.append('pickupStation', stationInfo.station);
      formData.append('customerName', shippingData.name);
      formData.append('customerEmail', shippingData.email);
      formData.append('customerPhone', shippingData.phone);
      
      // Vollständige Adresse zusammenstellen
      const fullAddress = `${shippingData.address}, ${shippingData.postalCode} ${shippingData.city}, ${shippingData.country}`;
      formData.append('shippingAddress', fullAddress);
      
      // Fotos hinzufügen (falls hochgeladen)
      if (shippingData.ticketPhoto) {
        formData.append('ticketPhoto', shippingData.ticketPhoto);
      }
      if (shippingData.dhlLabel) {
        formData.append('dhlLabelPhoto', shippingData.dhlLabel);
      }

      // API-Request senden
      const result = await apiCall('/luggage-shipping-request', {
        method: 'POST',
        body: formData
      });
      
      if (result.success) {
        // Bestätigung bleibt stehen (mit Referenznummer) – vorher verschwand sie nach
        // 3 Sekunden zusammen mit dem Formular, und der Kunde wusste nicht, ob es
        // angekommen ist.
        setSubmitted({ id: result.id, email: shippingData.email });
        setShowShippingForm(false);
        setShippingData({
          name: '', email: '', phone: '', address: '',
          city: '', postalCode: '', country: '',
          ticketPhoto: null, dhlLabel: null
        });
      } else {
        setSubmitMessage('❌ ' + (result.message || 'Unbekannter Fehler'));
      }
      
    } catch (error) {
      console.error('Fehler beim Senden der Versandanfrage:', error);
      setSubmitMessage('❌ ' + t('lug.errSend') + ': ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid vh-100"
         style={{ position: 'relative', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>

      {/* Sprachumschalter */}
      <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
        <button type="button" className="btn btn-light btn-sm shadow-sm" onClick={() => setShowLangMenu(!showLangMenu)}>
          {LANGUAGES.find(l => l.code === language)?.flag || '🇩🇪'} ▾
        </button>
        {showLangMenu && (
          <div className="bg-white shadow rounded mt-1" style={{ position: 'absolute', right: 0, minWidth: 160 }}>
            {LANGUAGES.map(lang => (
              <div key={lang.code}
                onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
                style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', background: language === lang.code ? '#e8f0fe' : 'transparent' }}>
                <span>{lang.flag}</span> <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Header */}
      <div className="row">
        <div className="col-12 text-center pt-4 pb-3">
          <div className="text-white">
            <h2 className="mb-2">
              <span style={{fontSize: '2rem'}}>🧳</span>
              <br />
              {t('lug.title')}
            </h2>
            <p className="mt-2 text-white-50">
              {t('lug.searchHint')}
            </p>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          
          {/* Suchformular */}
          <div className="card shadow-lg border-0 mb-4">
            <div className="card-body p-4">
              <form onSubmit={handleSearch}>
                <div className="mb-3">
                  <label htmlFor="ticketNumber" className="form-label fw-bold">
                    <i className="bi bi-ticket-perforated me-2"></i>
                    {t('lug.ticketLabel')}
                  </label>
                  <input
                    id="ticketNumber"
                    type="number"
                    className="form-control form-control-lg text-center"
                    placeholder={t('lug.ticketPh')}
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value)}
                    min="1"
                    max="12000"
                    required
                  />
                  <small className="form-text text-muted">
                    {t('lug.ticketRange')}
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                >
                  <i className="bi bi-search me-2"></i>
                  {t('lug.findStation')}
                </button>
              </form>
            </div>
          </div>

          {/* Keine Station gefunden */}
          {searched && !stationInfo && (
            <div className="card shadow-lg border-0 mb-4">
              <div className="card-body p-4 text-center">
                <div style={{ fontSize: '2.5rem' }} className="mb-2">🔎</div>
                <h5 className="fw-bold mb-2">{t('lug.notFoundTitle')}</h5>
                <p className="text-muted mb-0">{t('lug.notFoundText')}</p>
              </div>
            </div>
          )}

          {/* Stationsergebnis */}
          {stationInfo && (
            <div className="card shadow-lg border-0 mb-4">
              <div className="card-body p-4">
                <div className="text-center mb-3">
                  <div style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    lineHeight: '80px',
                    fontSize: '2.5rem',
                    color: 'white'
                  }}>
                    📍
                  </div>
                </div>
                
                <h4 className="text-center mb-3">
                  <strong>{stationInfo.name}</strong>
                </h4>
                
                <div className="text-center mb-3">
                  <span className="badge bg-primary fs-6 px-3 py-2">
                    {t('lug.pickupStation')}
                  </span>
                </div>
                
                <div className="d-grid gap-2">
                  <a 
                    href={stationInfo.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success btn-lg"
                  >
                    <i className="bi bi-geo-alt-fill me-2"></i>
                    {t('lug.openMaps')}
                  </a>

                  <button
                    className="btn btn-outline-warning btn-lg"
                    onClick={() => setShowShippingForm(true)}
                  >
                    <i className="bi bi-truck me-2"></i>
                    {t('lug.cantPickup')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bestätigung nach abgeschickter Versandanfrage */}
          {submitted && (
            <div className="card shadow-lg border-0 mb-4 border-success" style={{ borderLeft: '6px solid #28a745' }}>
              <div className="card-body p-4 text-center">
                <div style={{ fontSize: '2.5rem' }} className="mb-2">✅</div>
                <h5 className="fw-bold mb-2">{t('lug.successMsg')}</h5>
                <p className="mb-1">{t('lug.refLabel')} <strong>#{submitted.id}</strong> · {t('lug.ticketLabel')} <strong>{ticketNumber}</strong></p>
                <p className="text-muted small mb-0">{t('lug.confirmMail')} <strong>{submitted.email}</strong></p>
              </div>
            </div>
          )}

          {/* Versandformular */}
          {showShippingForm && (
            <div className="card shadow-lg border-0 mb-4">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="bi bi-truck me-2"></i>
                  {t('lug.shippingRequest')}
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleShippingSubmit}>
                  
                  {/* Persönliche Daten */}
                  <h6 className="mb-3 text-primary">
                    <i className="bi bi-person-fill me-2"></i>
                    {t('lug.personalData')}
                  </h6>

                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">{t('lug.fullName')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shippingData.name}
                        onChange={(e) => setShippingData(prev => ({...prev, name: e.target.value}))}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('lug.emailLabel')} *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={shippingData.email}
                        onChange={(e) => setShippingData(prev => ({...prev, email: e.target.value}))}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('lug.phoneLabel')}</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={shippingData.phone}
                        onChange={(e) => setShippingData(prev => ({...prev, phone: e.target.value}))}
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label">{t('lug.address')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shippingData.address}
                        onChange={(e) => setShippingData(prev => ({...prev, address: e.target.value}))}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('lug.postalCode')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shippingData.postalCode}
                        onChange={(e) => setShippingData(prev => ({...prev, postalCode: e.target.value}))}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('lug.city')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shippingData.city}
                        onChange={(e) => setShippingData(prev => ({...prev, city: e.target.value}))}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-4">
                    <div className="col-12">
                      <label className="form-label">{t('lug.country')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shippingData.country}
                        onChange={(e) => setShippingData(prev => ({...prev, country: e.target.value}))}
                        required
                      />
                    </div>
                  </div>

                  {/* Dokumente Upload */}
                  <h6 className="mb-3 text-primary">
                    <i className="bi bi-cloud-upload-fill me-2"></i>
                    {t('lug.requiredDocs')}
                  </h6>

                  <div className="mb-3">
                    <label className="form-label">
                      <strong>{t('lug.ticketPhoto')} *</strong>
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange('ticketPhoto', e.target.files[0])}
                      required
                    />
                    <small className="form-text text-muted">
                      {t('lug.ticketPhotoHint')}
                    </small>
                  </div>
                  
                  <div className="mb-4">
                    <label className="form-label">
                      <strong>{t('lug.dhlLabel')} *</strong>
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange('dhlLabel', e.target.files[0])}
                      required
                    />
                    <div className="mt-2">
                      <small className="form-text text-muted">
                        {t('lug.dhlBuyHint')}
                      </small>
                      <br />
                      <a 
                        href="https://www.dhl.de/de/privatkunden/pakete-versenden/online-frankieren.html?type=ShipmentEditorProductSelection&cid=c_dhl_AW-720_382&s_kwcid=AL!6093!10!77584499581988!77584760795191"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm mt-1"
                      >
                        <i className="bi bi-box-seam me-2"></i>
                        {t('lug.dhlBuy')}
                      </a>
                    </div>
                    <div className="alert alert-info mt-2">
                      <small>{t('lug.dhlImportant')}</small>
                    </div>
                  </div>

                  {/* Statusmeldung */}
                  {submitMessage && (
                    <div className={`alert ${submitMessage.includes('✅') ? 'alert-success' : 'alert-danger'} mb-3`}>
                      <div dangerouslySetInnerHTML={{ __html: submitMessage.replace(/\n/g, '<br />') }} />
                    </div>
                  )}

                  <div className="d-grid gap-2">
                    <button 
                      type="submit" 
                      className="btn btn-warning btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t('lug.sending')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill me-2"></i>
                          {t('lug.send')}
                        </>
                      )}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowShippingForm(false)}
                      disabled={isSubmitting}
                    >
                      {t('lug.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Zurück-Button */}
          <div className="text-center mb-4">
            <a href="/" className="btn btn-outline-light">
              <i className="bi bi-arrow-left me-2"></i>
              {t('lug.back')}
            </a>
          </div>
          <ImpressumFooter />
        </div>
      </div>
    </div>
  );
};

export default LuggageSearch;